import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AdvanceTableResponsive from '../../../components/common/advance-table/AdvanceTableResponsive';
import CardDropdown from '../../../components/common/CardDropdown';
import OrderDeliveryStatusCell from './common/OrderDeliveryStatusCell';
import PendingListStatusBadge, { PendingThemeBadge } from './common/PendingListStatusBadge';
import PendingOrderIdCell from './common/PendingOrderIdCell';
import { PENDING_TAB_BADGE, PENDING_TABLE_PER_PAGE } from './common/pendingTableConfig';
import PendingListLoadingState from './common/PendingListLoadingState';
import useAdvanceTable from '../../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../../providers/AdvanceTableProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { getOrderListPaymentStatusLabel } from '../../../utils/order/orderListStatusFilterOptions';
import { normalizeStoredPaymentStatus } from '../../../utils/order/orderPaymentStatus';
import PendingListEmptyState from './common/PendingListEmptyState';
import { PendingKeywordHighlight, PendingTotalPriceHighlight } from './common/PendingKeywordHighlight';
import PendingListTableFooter from './common/PendingListTableFooter';
import PendingListToolbar from './common/PendingListToolbar';
import { buildPendingActivityAtColumn, PENDING_ACTIVITY_AT_SORT } from './common/pendingActivityAtColumn';
import { usePendingKeywordFilter } from './common/usePendingKeywordFilter';
import { socket } from '../../../socket';
import ProcessingOrderModal from './modal/ProcessingOrderModal';

const RecentOrderPendings = ({ scope = 'finance' }) => {
	const [isOpenPendingModal, setIsOpenPendingModal] = useState(false);

	const formatDateTime = useCallback((date) => {
		return moment(date).format('YYYY-MM-DD HH:mm');
	}, []);

	const {
		processingPendingBuyerOrders = [],
		processingPendingFinanceOrders = [],
		loadingProcessingPending,
		uninitializedProcessingPending,
		setCurrentOrderOIdState,
	} = useOrderContext();

	const handleOrderClick = useCallback(
		(orderOId) => {
			setIsOpenPendingModal(true);
			setCurrentOrderOIdState(orderOId);
			socket.emit('join order', orderOId);
		},
		[setCurrentOrderOIdState],
	);

	const columnProfile = scope === 'buyer' ? 'buyer' : 'finance';

	const columns = useMemo(() => {
		const userColumns = [
			{
				accessorKey: 'orderId',
				header: '订单编号',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-start' },
					cellProps: {
						className: 'fw-semibold text-start',
					},
				},
				cell: ({ row: { original } }) => {
					const { orderId, _id } = original;
					return (
						<PendingOrderIdCell
							orderId={orderId}
							orderMongoId={_id}
							badgeVariant={PENDING_TAB_BADGE.recent}
							onOrderClick={handleOrderClick}
							order={original}
						/>
					);
				},
			},
			{
				accessorKey: 'customerInfo.customerName',
				header: '客户名称',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => {
					const { customerInfo } = original;
					return (
						<span className={`link-${PENDING_TAB_BADGE.recent}`}>
							<PendingKeywordHighlight text={customerInfo?.customerName} />
						</span>
					);
				},
			},
			{
				accessorKey: 'deliveryStatus',
				header: '物流状态',
				meta: {
					headerProps: { className: 'text-center text-900' },
				},
				cell: ({ row: { original } }) => (
					<OrderDeliveryStatusCell deliveryStatus={original.deliveryStatus} badgeVariant={PENDING_TAB_BADGE.recent} />
				),
			},
			{
				accessorKey: 'saleUser.name',
				header: '销售员',
				meta: {
					headerProps: { className: 'text-center text-900' },
				},
				cell: ({ row: { original } }) => {
					const { saleUser } = original;
					return <span className='fs-11 fw-normal'>{saleUser?.name || '-'}</span>;
				},
			},
			{
				accessorKey: 'assistantUser.name',
				header: '销售助理',
				meta: {
					headerProps: { className: 'text-center text-900' },
				},
				cell: ({ row: { original } }) => {
					const { assistantUser } = original;
					return <span className='fs-11 fw-normal'>{assistantUser?.name || '-'}</span>;
				},
			},
			{
				accessorKey: 'totalPrice',
				header: '总价',
				meta: {
					headerProps: {
						className: 'text-end text-900',
					},
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					return <PendingTotalPriceHighlight totalPrice={original.totalPrice} className='fs-11 fw-normal' />;
				},
			},
			buildPendingActivityAtColumn(formatDateTime),
			{
				accessorKey: 'createdAt',
				header: '创建时间',
				meta: {
					headerProps: {
						className: 'text-end text-900',
					},
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					return <span className='fs-11 fw-normal'>{original?.createdAt ? formatDateTime(original.createdAt) : '-'}</span>;
				},
			},
			{
				accessorKey: 'none',
				header: '',
				enableSorting: false,
				meta: {
					cellProps: {
						className: 'text-end py-2',
					},
				},
				cell: ({ row: { original } }) => {
					return (
						<CardDropdown drop='start'>
							<div className='py-2'>
								<Dropdown.Item as='button' type='button' className='w-100 text-start' onClick={() => handleOrderClick(original._id)}>
									查看
								</Dropdown.Item>
							</div>
						</CardDropdown>
					);
				},
			},
		];
		if (columnProfile === 'manager') {
			const b = [
				{
					accessorKey: 'saleUser.name',
					header: '销售员',
					meta: {
						headerProps: { className: 'text-center text-900' },
					},
					cell: ({ row: { original } }) => {
						const { saleUser } = original;
						return <span className='fs-11 fw-normal'>{saleUser?.name || '-'}</span>;
					},
				},
				{
					accessorKey: 'assistantUser.name',
					header: '销售助理',
					meta: {
						headerProps: { className: 'text-center text-900' },
					},
					cell: ({ row: { original } }) => {
						const { assistantUser } = original;
						return <span className='fs-11 fw-normal'>{assistantUser?.name || '-'}</span>;
					},
				},
			];
			userColumns.splice(1, 1, ...b);
		}
		else if (columnProfile === 'sale') {
			const x = [
				{
					accessorKey: 'assistantUser.name',
					header: '销售助理',
					meta: {
						headerProps: { className: 'text-center text-900' },
					},
					cell: ({ row: { original } }) => {
						const { assistantUser } = original;
						return <span className='fs-11 fw-normal'>{assistantUser?.name}</span>;
					},
				},
				{
					accessorKey: 'orderTask',
					header: '任务',
					meta: {
						headerProps: { className: 'text-center text-900' },
					},
					cell: ({ row: { original } }) => {
						const { orderTask, _id } = original;
						return (
							<PendingThemeBadge bg='danger' className='me-2'>
								<FontAwesomeIcon icon='fa-solid fa-bolt' className='me-2' />
								<span className='fs-11'>
									<Link
										to='#'
										onClick={(e) => {
											e.preventDefault();
											handleOrderClick(_id);
										}}
										className='text-decoration-none'
									>
										{orderTask}
									</Link>
								</span>
							</PendingThemeBadge>
						);
					},
				},
			];
			userColumns.splice(1, 1, ...x);
		}
		else if (columnProfile === 'assistant') {
			const a = [
				{
					accessorKey: 'saleUser.name',
					header: '销售员',
					meta: {
						headerProps: { className: 'text-center text-900' },
					},
					cell: ({ row: { original } }) => {
						const { saleUser } = original;
						return <span className='fs-11 fw-normal'>{saleUser.name}</span>;
					},
				},
				{
					accessorKey: 'assistantUser.name',
					header: '销售助理',
					meta: {
						headerProps: { className: 'text-center text-900' },
					},
					cell: ({ row: { original } }) => {
						const { assistantUser } = original;
						return <span className='fs-11 fw-normal'>{assistantUser?.name}</span>;
					},
				},
				{
					accessorKey: 'orderTask',
					header: '任务',
					meta: {
						headerProps: { className: 'text-center text-900' },
					},
					cell: ({ row: { original } }) => {
						const { orderTask, _id } = original;
						return (
							<PendingThemeBadge bg='danger' className='me-2'>
								<FontAwesomeIcon icon='fa-solid fa-bolt' className='me-2' />
								<span className='fs-11'>
									<Link
										to='#'
										onClick={(e) => {
											e.preventDefault();
											handleOrderClick(_id);
										}}
										className='text-decoration-none'
									>
										{orderTask}
									</Link>
								</span>
							</PendingThemeBadge>
						);
					},
				},
			];
			userColumns.splice(1, 1, ...a);
		}
		else if (columnProfile === 'buyer') {
			const buyerColumns = [
				{
					accessorKey: 'customerInfo',
					header: '客户名称',
					meta: {
						headerProps: { className: 'pe-3 text-900 text-start' },
						cellProps: { className: 'text-start' },
					},
					cell: ({ row: { original } }) => {
						const { customerInfo } = original;
						return (
							<span className={`link-${PENDING_TAB_BADGE.recent}`}>
								<PendingKeywordHighlight text={customerInfo?.customerName} />
							</span>
						);
					},
				},
				{
					accessorKey: 'paymentStatus',
					header: '支付状态',
					meta: {
						headerProps: { className: 'text-900' },
					},
					cell: ({ row: { original } }) => {
						const { paymentStatus, isCompleted } = original;
						const canonical = normalizeStoredPaymentStatus(paymentStatus, original);
						const label = getOrderListPaymentStatusLabel(canonical);

						if (!isCompleted && canonical === 'payComplete') {
							return <PendingListStatusBadge bg='success' text='当前无应收' icon='check' />;
						}

						if (!label) {
							return '-';
						}

						const bg = canonical === 'payComplete' ? 'success' : 'warning';
						const icon = canonical === 'payComplete' ? 'check' : 'stream';
						return <PendingListStatusBadge bg={bg} text={label} pill={canonical === 'payComplete'} icon={icon} />;
					},
				},
			];
			userColumns.splice(1, 1, ...buyerColumns);
			return userColumns.filter((column) => column.accessorKey !== 'currentAccountsReceivable');
		}
		return userColumns;
	}, [columnProfile, handleOrderClick, formatDateTime]);

	const currentPendingOrders = scope === 'buyer' ? processingPendingBuyerOrders : processingPendingFinanceOrders;

	const { keyword, hasKeyword, filteredOrders } = usePendingKeywordFilter(currentPendingOrders);

	const table = useAdvanceTable({
		data: filteredOrders,
		columns: columns,
		sortable: true,
		pagination: true,
		perPage: PENDING_TABLE_PER_PAGE,
		initialState: {
			sorting: PENDING_ACTIVITY_AT_SORT,
		},
	});

	const isRoleLoading = loadingProcessingPending || uninitializedProcessingPending;

	if (isRoleLoading) {
		return <PendingListLoadingState />;
	}

	const showFooter = currentPendingOrders.length > 0 || hasKeyword;

	return (
		<>
			<AdvanceTableProvider {...table}>
				<PendingListToolbar>
					{filteredOrders.length === 0 ? (
						<PendingListEmptyState
							emptyTitle={hasKeyword ? '无匹配订单' : '暂无待处理'}
							emptyDescription={hasKeyword ? `当前待办中没有包含「${keyword}」的订单。` : '目前没有需要你处理的订单。'}
						/>
					) : (
						<AdvanceTableResponsive
							headerClassName='bg-200 text-nowrap align-middle font-sans-serif'
							rowClassName='btn-reveal-trigger text-nowrap align-middle'
							tableProps={{
								className: 'fs-10 fw-semibold mb-0 overflow-hidden',
							}}
						/>
					)}
					{showFooter ? <PendingListTableFooter showTableControls={filteredOrders.length > 0} /> : null}
				</PendingListToolbar>
			</AdvanceTableProvider>
			<ProcessingOrderModal setIsOpenPendingModal={setIsOpenPendingModal} isOpenPendingModal={isOpenPendingModal} />
		</>
	);
};

export default RecentOrderPendings;
